// The halls' built water (docs/166 C2, the cisterns, keeps, temples and shrines): what a pool's edge is made of
// where the kit's own board script draws it, instead of the shared ragged bank. Ported from each kit's
// `*_board.py.txt`: the cistern's `basin` kerb and its deep fading into the shallows, the keeps' bank fray at
// half reach, the ashlands' squared `tank`, the temples' raised kerb and inner step, the shrines' wet line and
// shelf of growth. Only how the squares look: which squares are water is the forge's.
import { EDGE_DEPTHS } from '../../../ui/screens/map/groundEdges';
import { Canvas, hex, type RGBA } from './canvas';
import { fromOklab } from './colour';
import { C, type Ctx } from './context';
import { groundOf, isLiquid, shoreOf, type Ground } from './ground';

type Grounds = ReadonlyArray<Ground | undefined>;

export const lab = (L: number, a: number, b: number): RGBA => fromOklab(L, a, b);

/** `p` moved toward `c` by `a` (0..1), as the board scripts' `blend`. */
function mix(out: Canvas, X: number, Y: number, c: RGBA | readonly number[], a: number): void {
  if (!out.inside(X, Y) || a <= 0) return;
  const i = (Y * out.w + X) * 4, p = out.px;
  p[i] = p[i]! + (c[0]! - p[i]!) * a; p[i + 1] = p[i + 1]! + (c[1]! - p[i + 1]!) * a; p[i + 2] = p[i + 2]! + (c[2]! - p[i + 2]!) * a;
}

/** The squares as the water scripts see them: liquid, dry floor (land) and wall. */
class Water {
  readonly w: number;
  readonly h: number;
  constructor(readonly ctx: Ctx, readonly grounds: Grounds) { this.w = ctx.sq.w; this.h = ctx.sq.h; }
  g(x: number, y: number): Ground | undefined { return this.ctx.sq.on(x, y) ? this.grounds[y * this.w + x] : undefined; }
  wet(x: number, y: number): boolean { const g = this.g(x, y); return !!g && isLiquid(g.name); }
  deep(x: number, y: number): boolean { return this.g(x, y)?.name === 'deep'; }
  /** A floor square: on the board, with a ground, not liquid, not a wall. */
  dry(x: number, y: number): boolean { const g = this.g(x, y); return !!g && !isLiquid(g.name) && !this.ctx.sq.solid(x, y); }
  wall(x: number, y: number): boolean { return this.ctx.sq.solid(x, y); }
  any(): boolean { for (const g of this.grounds) if (g && isLiquid(g.name)) return true; return false; }
}

// ---- the deep fading into the shallows (the cisterns, `fade` in `cistern_board.py.txt`) ----------------------------

/** Bands of (distance under, share of the shallow water's own tile): the wetland cistern's and every later one's. */
const BANDS: Record<string, ReadonlyArray<readonly [number, number]>> = {
  wide: [[1, 1], [4, 0.84], [8, 0.68], [12, 0.52], [16, 0.36], [20, 0.22], [24, 0.1]],
  even: [[1, 1], [6, 0.7], [12, 0.46], [19, 0.22]],
};

/**
 * The deep fades into the water beside it by rounded ragged bands (a field over the board, so no square's edge
 * shows): each deep pixel takes some of the water tile's own pixel by its distance from the nearest water square.
 * `bands` the later kits' wobble that only pushes inward; none, the boreal cistern's two steps.
 */
export function fadeDeep(ctx: Ctx, out: Canvas, grounds: Grounds, bands?: keyof typeof BANDS): void {
  const W = new Water(ctx, grounds);
  if (!ctx.has('water')) return;
  const B = bands ? BANDS[bands]! : undefined;
  for (let y = 0; y < W.h; y++) for (let x = 0; x < W.w; x++) {
    if (!W.deep(x, y)) continue;
    const near: Array<[number, number]> = [];
    for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) if (W.wet(x + dx, y + dy) && !W.deep(x + dx, y + dy)) near.push([x + dx, y + dy]);
    if (!near.length) continue;
    const water = groundOf(ctx, 'water', 'water', x, y)!;
    for (let iy = 0; iy < C; iy++) for (let ix = 0; ix < C; ix++) {
      const X = x * C + ix, Y = y * C + iy;
      let d0 = 99;
      for (const [a, b] of near) d0 = Math.min(d0, Math.hypot(Math.max(a * C - X, 0, X - (a * C + C - 1)), Math.max(b * C - Y, 0, Y - (b * C + C - 1))));
      let t: number;
      if (B) {
        const d = d0 - 7.2 + (4.2 * Math.sin(X / 7.3 + 1.7 * Math.sin(Y / 11.1)) + 3.0 * Math.sin(Y / 5.7 + X / 9.9));
        t = B.find(([lim]) => d < lim)?.[1] ?? 0;
      } else {
        const d = d0 + 2.2 * Math.sin(X / 5.3 + 1.7 * Math.sin(Y / 9.1)) + 1.6 * Math.sin(Y / 3.7 + X / 7.9);
        t = d >= 11 ? 0 : d < 5 ? 0.6 : 0.28;
      }
      if (t) mix(out, X, Y, Canvas.sample(water.piece, ix, iy, water.turn), t);
    }
  }
}

// ---- the cistern's kerb (look.json `basin`) -------------------------------------------------------------------------

interface Basin { lit: string; stone?: string; body: string; shade: string; joint: string; mortar?: string; shadow: number[] | string; block?: [number, number]; block_steps?: number[]; proud_every?: number }
/** The boreal cistern has no `basin` in its look.json: its board script's own colours. */
const BOREAL_BASIN: Basin = { lit: '#58838a', body: '#3e6670', shade: '#2a4852', joint: '#14282e', shadow: [6, 26, 32, 120] };

/** A cistern's water edge: lips on the near side and the sides, the kerb's face on the far side, the dark a wall throws in the water. */
export function cisternKerb(ctx: Ctx, out: Canvas, grounds: Grounds): void {
  const W = new Water(ctx, grounds);
  const K = (ctx.look['basin'] as Basin | undefined) ?? BOREAL_BASIN;
  const biome = ctx.set.biome;
  const kc = { lit: hex(K.lit), body: hex(K.body), shade: hex(K.shade), joint: hex(K.joint), stone: hex(K.stone ?? K.lit), mortar: hex(K.mortar ?? K.joint) };
  const sh = typeof K.shadow === 'string' ? hex(K.shadow) : (K.shadow as unknown as RGBA);
  // the boreal's lips are its lit, body and shade stones; the later kits' are all `stone`
  const own = !K.stone;
  const lipS = own ? kc.lit : kc.stone, lipW = own ? kc.body : kc.stone, lipE = own ? kc.shade : kc.stone;
  // blocks along the far kerb: lengths from `block`, whole steps of tone from `block_steps`, a few a pixel proud
  const blocks: Array<{ end: boolean; step: number; proud: boolean }> = [];
  if (K.block) {
    const span = K.block[1] - K.block[0] + 1, steps = K.block_steps ?? [0];
    for (let x0 = 0, b = 0; x0 < W.w * C; b++) {
      const n = K.block[0] + ctx.roll('kerb', b, 0) % span, step = steps[ctx.roll('kerbt', b, 0) % steps.length]!, proud = ctx.roll('kerbp', b, 0) % (K.proud_every ?? 5) === 0;
      for (let i = 0; i < n; i++) blocks.push({ end: i === n - 1, step, proud: proud && i > 0 && i < n - 1 });
      x0 += n;
    }
  }
  const blockAt = (X: number): { end: boolean; step: number; proud: boolean } | undefined => blocks[X];
  const tone = (c: RGBA, s: number): RGBA => [Math.max(0, Math.min(255, c[0] + s)), Math.max(0, Math.min(255, c[1] + s)), Math.max(0, Math.min(255, c[2] + s)), 255];
  for (let y = 0; y < W.h; y++) for (let x = 0; x < W.w; x++) {
    if (!W.wet(x, y)) continue;
    const X = x * C, Y = y * C;
    if (W.wall(x, y - 1) && ctx.sq.on(x, y - 1)) for (let i = 0; i < C; i++) for (let d = 0; d < 4; d++) mix(out, X + i, Y + 5 + d, sh, (90 - d * 20) / 255);
    if (W.dry(x, y + 1)) for (let i = 0; i < C; i++) { out.set(X + i, Y + C - 1, lipS); out.set(X + i, Y + C - 2, kc.joint); }
    if (W.dry(x - 1, y)) for (let i = 0; i < C; i++) { out.set(X, Y + i, lipW); out.set(X + 1, Y + i, kc.joint); mix(out, X + 2, Y + i, sh, 90 / 255); }
    if (W.dry(x + 1, y)) for (let i = 0; i < C; i++) { out.set(X + C - 1, Y + i, lipE); out.set(X + C - 2, Y + i, kc.joint); }
    if (!W.dry(x, y - 1)) continue;
    // the far bank: the kerb's face stands above the water and shades it
    for (let i = 0; i < C; i++) {
      const Xi = X + i, bl = blockAt(Xi);
      if (bl) {
        if (bl.end) for (let d = 0; d < 4; d++) out.set(Xi, Y + d, kc.mortar);
        else {
          out.set(Xi, Y, tone(ctx.roll('kerbc', Xi, 0) % 9 === 0 ? kc.body : kc.lit, bl.step));
          out.set(Xi, Y + 1, tone(kc.body, bl.step)); out.set(Xi, Y + 2, tone(kc.body, bl.step)); out.set(Xi, Y + 3, tone(kc.shade, bl.step));
          if (bl.proud) out.set(Xi, Y - 1, tone(kc.lit, bl.step));
          // laterite is pitted (the jungle cistern)
          if (biome === 'jungle') { if (ctx.roll('kerbpit', Xi, 0) % 8 === 0) out.set(Xi, Y + 1, kc.mortar); if (ctx.roll('kerbpit2', Xi, 0) % 11 === 0) out.set(Xi, Y + 2, kc.mortar); }
        }
      } else if (biome === 'wetland') {
        // a limestone cap over thin old brick, a lime joint every 6 pixels
        const g = Xi % 6 === 5, g2 = (Xi + 3) % 6 === 5;
        out.set(Xi, Y, kc.lit); out.set(Xi, Y + 1, g ? kc.mortar : kc.body); out.set(Xi, Y + 2, kc.mortar); out.set(Xi, Y + 3, g2 ? kc.mortar : kc.shade);
      } else {
        const g = Xi % 12 === 11;
        out.set(Xi, Y, g ? kc.joint : kc.lit); out.set(Xi, Y + 1, g ? kc.joint : kc.body); out.set(Xi, Y + 2, g ? kc.joint : kc.body); out.set(Xi, Y + 3, g ? kc.joint : kc.shade);
      }
      out.set(Xi, Y + 4, kc.joint);
      for (let d = 0; d < 3; d++) mix(out, Xi, Y + 5 + d, sh, (150 - d * 45) / 255);
    }
  }
}

// ---- the keeps' bank at half reach (`SHORT` in `keep_board.py.txt`) -----------------------------------------------

const SHORT = EDGE_DEPTHS.map((row) => row.map((e) => (e > 3 ? 3 + Math.floor((e - 3) / 2) : e)));

/** The shared bank (ET6) with the fray at half reach and walls not counted as land: a flooded floor soaks up to the flags. */
export function shortBanks(ctx: Ctx, out: Canvas, grounds: Grounds): void {
  const W = new Water(ctx, grounds);
  const [lip, wet, rim] = shoreOf(ctx);
  for (let y = 0; y < W.h; y++) for (let x = 0; x < W.w; x++) {
    if (!W.wet(x, y)) continue;
    const sides: Array<[number, number, string]> = [[0, -1, 'n'], [0, 1, 's'], [-1, 0, 'w'], [1, 0, 'e']];
    const land = sides.filter(([dx, dy]) => W.dry(x + dx, y + dy));
    const corners = [[-1, -1], [1, -1], [-1, 1], [1, 1]].filter(([dx, dy]) => W.dry(x + dx!, y + dy!));
    if (!land.length && !corners.length) continue;
    const prof = land.map(([, , s]) => SHORT[ctx.roll(`bank:${s}`, x, y) % SHORT.length]!);
    const fl = groundOf(ctx, 'floor', 'floor', x + 1, y);
    for (let iy = 0; iy < C; iy++) for (let ix = 0; ix < C; ix++) {
      let d = 99;
      land.forEach(([, , s], k) => {
        const [i, kk] = s === 'n' ? [ix, iy] : s === 's' ? [ix, C - 1 - iy] : s === 'w' ? [iy, ix] : [iy, C - 1 - ix];
        d = Math.min(d, kk - prof[k]![i]!);
      });
      for (const [dx, dy] of corners) d = Math.min(d, Math.max(dx! < 0 ? ix : C - 1 - ix, dy! < 0 ? iy : C - 1 - iy) - 3);
      const X = x * C + ix, Y = y * C + iy;
      if (d < -1 && fl) out.copy(X, Y, fl.piece, ix, iy, fl.turn);
      else if (d < 1) out.set(X, Y, lip);
      else if (d === 1) out.set(X, Y, wet);
      else if (d === 2 && (ix + iy) % 3) out.set(X, Y, rim);
    }
  }
}

// ---- the ashlands' squared tank (look.json `tank`, `keep_board.py.txt`) ---------------------------------------------

interface Tank { lit: string; body: string; shade: string; wet: string; rim: string; stain: string; joint_every: number }

/** A squared kerb on the square's grid: 4 rows on the far side with its dark inner face, 2 on the others, a wet line and dim rim inside, rust flecks on the land side. `banded`: the ashlands shrine's lengths in the three stones. */
export function tankKerb(ctx: Ctx, out: Canvas, grounds: Grounds, banded = false): void {
  const W = new Water(ctx, grounds);
  const T0 = ctx.look['tank'] as Tank;
  const T = { lit: hex(T0.lit), body: hex(T0.body), shade: hex(T0.shade), wet: hex(T0.wet), rim: hex(T0.rim), stain: hex(T0.stain) };
  const EV = banded ? 12 : T0.joint_every;
  const OX = (L: number): RGBA => lab(L, 0.04, 0.029), TF = (L: number): RGBA => lab(L, 0.008, 0.006);
  const BAND: ReadonlyArray<readonly [RGBA, RGBA]> = [[T.lit, T.body], [OX(0.44), OX(0.37)], [T.lit, T.body], [TF(0.6), TF(0.5)]];
  const seam = banded ? hex('#1e1a1b') : T.shade;
  for (let y = 0; y < W.h; y++) for (let x = 0; x < W.w; x++) {
    if (!W.wet(x, y)) continue;
    const ln = W.dry(x, y - 1), ls = W.dry(x, y + 1), lw = W.dry(x - 1, y), le = W.dry(x + 1, y);
    const cs = [[-1, -1], [1, -1], [-1, 1], [1, 1]].filter(([dx, dy]) => W.dry(x + dx!, y + dy!) && !W.dry(x + dx!, y) && !W.dry(x, y + dy!));
    if (!ln && !ls && !lw && !le && !cs.length) continue;
    for (let iy = 0; iy < C; iy++) for (let ix = 0; ix < C; ix++) {
      let n = ln ? iy : 99, s = ls ? C - 1 - iy : 99;
      const w = lw ? ix : 99, e = le ? C - 1 - ix : 99;
      // a land corner: the kerb turns squarely round it
      for (const [dx, dy] of cs) {
        const kk = Math.max(dx! < 0 ? ix : C - 1 - ix, dy! < 0 ? iy : C - 1 - iy);
        if (dy! < 0) n = Math.min(n, kk); else s = Math.min(s, kk);
      }
      const d = Math.min(n, s, w, e);
      if (d >= 99) continue;
      const wide = n <= Math.min(s, w, e) ? 4 : 2;
      const along = n === d || s === d ? x * C + ix : y * C + iy;
      const st = banded ? BAND[Math.floor(along / EV) % 4]! : [T.lit, T.body] as const;
      const X = x * C + ix, Y = y * C + iy;
      if (d < wide) {
        let c = wide === 4 ? (d === 0 ? st[0] : d === 1 ? st[1] : d === 2 ? T.shade : T.wet) : d === 0 ? st[0] : st[1];
        if (along % EV === EV - 1 && d < 2) c = seam;
        out.set(X, Y, c);
      } else if (d === wide && wide === 2) out.set(X, Y, T.wet);
      else if (d === wide + 1 && (ix + iy) % 3) out.set(X, Y, T.rim);
    }
    // rust: broken flecks on the flags beside the kerb, never a line
    for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0]] as const) {
      if (!W.dry(x + dx, y + dy)) continue;
      for (let k = 0; k < 3; k++) {
        const v = ctx.roll(`rust${dx}${dy}${k}`, x, y);
        if (v % 3) continue;
        const a = v % C, dd = 1 + ((v >>> 8) % 2);
        const qx = x * C + (dx === 0 ? a : dx < 0 ? -dd : C - 1 + dd), qy = y * C + (dx !== 0 ? a : dy < 0 ? -dd : C - 1 + dd);
        out.set(qx, qy, T.stain);
      }
    }
  }
}

// ---- the temples' raised kerb round a tank and its inner step (`temple_board.py.txt`) -------------------------------

/** The kerb stone's hue (a, b) and the wet line's, per biome, from each temple's `kerb_col`. */
const TEMPLE_STONE: Record<string, { a: number; b: number; joint: RGBA; wet: RGBA; step: [RGBA, RGBA] }> = {
  boreal: { a: 0.012, b: 0.04, joint: lab(0.24, 0.008, 0.036), wet: lab(0.16, 0.012, 0.02), step: [lab(0.2, -0.026, -0.026), lab(0.38, -0.04, -0.028)] },
  desert: { a: 0.05, b: 0.046, joint: lab(0.3, 0.036, 0.026), wet: lab(0.16, 0.018, 0.012), step: [lab(0.24, -0.018, -0.006), lab(0.46, -0.026, -0.004)] },
  highland: { a: 0.044, b: 0.032, joint: lab(0.3, 0.044, 0.032), wet: lab(0.16, 0.018, 0.012), step: [lab(0.24, -0.018, -0.006), lab(0.46, -0.026, -0.004)] },
  jungle: { a: 0.032, b: 0.024, joint: lab(0.3, 0.032, 0.024), wet: lab(0.16, 0.014, 0.01), step: [lab(0.24, -0.018, -0.006), lab(0.46, -0.026, -0.004)] },
  underdark: { a: 0.008, b: 0.03, joint: lab(0.3, 0.008, 0.03), wet: lab(0.16, 0.002, -0.006), step: [lab(0.22, -0.018, -0.014), lab(0.42, -0.026, -0.016)] },
  wetland: { a: 0.011, b: 0.042, joint: lab(0.3, 0.009, 0.03), wet: lab(0.16, 0.012, 0.02), step: [lab(0.24, -0.018, -0.006), lab(0.46, -0.026, -0.004)] },
};
/** The jungle shrine's holy tank: cut sandstone a step of tone apart (its own board script). */
const SHRINE_JUNGLE = { a: 0.02, b: 0.05, joint: lab(0.28, 0.018, 0.04), wet: lab(0.14, -0.01, 0.0), step: [lab(0.22, -0.02, -0.01), lab(0.4, -0.03, -0.01)] as [RGBA, RGBA] };

/**
 * A raised kerb 5 pixels wide on the floor round the water, following the forge's squares (the hand board's
 * tank is a rectangle; a forge pool is whatever shape the forge made): lit on its outer edge, its inner course in
 * shade, a joint every 16 pixels, a dark wet line just inside, a shadow on the floor below its near side; then the
 * deep's step, a dark line on its far sides and a lit one on its near.
 */
export function templeKerb(ctx: Ctx, out: Canvas, grounds: Grounds, stone = TEMPLE_STONE[ctx.set.biome] ?? TEMPLE_STONE['boreal']!): void {
  const W = new Water(ctx, grounds);
  const K = 5, SHADOW: RGBA = [10, 8, 5, 255];
  const col = (d: number, along: number): RGBA => {
    if (along % 16 === 15 && d > 0 && d < K - 1) return stone.joint;
    return lab(d === 0 ? 0.56 : d < K - 1 ? 0.47 : 0.3, stone.a, stone.b);
  };
  for (let y = 0; y < W.h; y++) for (let x = 0; x < W.w; x++) {
    if (!W.wet(x, y)) continue;
    const X0 = x * C, Y0 = y * C;
    // the far side (the floor square above): the kerb's top with its outer edge lit, the wet line in the water's first row
    if (W.dry(x, y - 1)) for (let i = 0; i < C; i++) {
      for (let d = 0; d < K; d++) out.set(X0 + i, Y0 - K + d, col(d, X0 + i));
      out.set(X0 + i, Y0, stone.wet);
    }
    // the near side (below): the kerb's lit top meets the water, its shadow on the floor beyond
    if (W.dry(x, y + 1)) for (let i = 0; i < C; i++) {
      for (let d = 0; d < K; d++) out.set(X0 + i, Y0 + C + d, col(d, X0 + i));
      for (let d = 0; d < 3; d++) mix(out, X0 + i, Y0 + C + K + d, SHADOW, 0.46 - d * 0.13);
    }
    if (W.dry(x - 1, y)) for (let j = 0; j < C; j++) for (let d = 0; d < K; d++) out.set(X0 - K + d, Y0 + j, col(d, Y0 + j));
    if (W.dry(x + 1, y)) for (let j = 0; j < C; j++) {
      out.set(X0 + C - 1, Y0 + j, stone.wet);
      for (let d = 0; d < K; d++) out.set(X0 + C + d, Y0 + j, col(K - 1 - d, Y0 + j));
      for (let d = 0; d < 2; d++) mix(out, X0 + C + K + d, Y0 + j, SHADOW, 0.36 - d * 0.14);
    }
    // the kerb turns round an outer corner of the water: fill the floor corner square's corner
    for (const [dx, dy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
      if (!W.dry(x + dx, y + dy) || !W.dry(x + dx, y) || !W.dry(x, y + dy)) continue;
      const cx = dx < 0 ? X0 - K : X0 + C, cy = dy < 0 ? Y0 - K : Y0 + C;
      for (let v = 0; v < K; v++) for (let u = 0; u < K; u++) {
        const du = dx < 0 ? u : K - 1 - u, dv = dy < 0 ? v : K - 1 - v;
        out.set(cx + u, cy + v, col(Math.min(du, dv), 0));
      }
    }
    // the inner step round the deep
    if (W.deep(x, y)) {
      if (W.wet(x, y - 1) && !W.deep(x, y - 1)) for (let i = 0; i < C; i++) out.set(X0 + i, Y0, stone.step[0]);
      if (W.wet(x - 1, y) && !W.deep(x - 1, y)) for (let j = 0; j < C; j++) out.set(X0, Y0 + j, stone.step[0]);
      if (W.wet(x, y + 1) && !W.deep(x, y + 1)) for (let i = 0; i < C; i++) out.set(X0 + i, Y0 + C - 1, stone.step[1]);
      if (W.wet(x + 1, y) && !W.deep(x + 1, y)) for (let j = 0; j < C; j++) out.set(X0 + C - 1, Y0 + j, stone.step[1]);
    }
  }
}

export const shrineJungleKerb = (ctx: Ctx, out: Canvas, grounds: Grounds): void => templeKerb(ctx, out, grounds, SHRINE_JUNGLE);

// ---- the shrines' spring: a dark wet line and a ragged shelf of the floor's growth (`shrine_board.py.txt`) -----------

/** The wet line's colour per shrine: the board scripts' `lab(.18, …)`, in the kit's hue. */
const SHRINE_WET: Record<string, RGBA> = {
  boreal: lab(0.18, -0.028, 0.012), wetland: lab(0.17, -0.02, 0.016), highland: lab(0.18, -0.02, 0.008), desert: lab(0.19, -0.01, 0.02), underdark: lab(0.15, -0.02, -0.004),
};

/**
 * A pool that is a spring and not a square: the floor's growth (`floor_patch`) creeps over its edge in a ragged
 * shelf, then a dark wet line, then the water; the shelf runs on a little onto the floor beside it.
 */
export function shrineShelf(ctx: Ctx, out: Canvas, grounds: Grounds): void {
  const W = new Water(ctx, grounds);
  const wetC = SHRINE_WET[ctx.set.biome] ?? lab(0.18, -0.02, 0.01);
  const moss = ctx.all('floor_patch');
  const tiles = new Map<number, (typeof moss)[number]>();
  const mossAt = (X: number, Y: number): RGBA | undefined => {
    if (!moss.length) return undefined;
    const x = Math.floor(X / C), y = Math.floor(Y / C), k = y * W.w + x;
    let p = tiles.get(k);
    if (!p) tiles.set(k, (p = moss[ctx.roll('shelf', x, y) % moss.length]!));
    return Canvas.sample(p, X % C, Y % C);
  };
  for (let y = 0; y < W.h; y++) for (let x = 0; x < W.w; x++) {
    const wet = W.wet(x, y);
    if (!wet && !W.dry(x, y)) continue;
    // on the water: distance from the land sides (the shared bank's ragged profile); on the floor: distance from the water sides
    const sides = ([[0, -1, 'n'], [0, 1, 's'], [-1, 0, 'w'], [1, 0, 'e']] as const).filter(([dx, dy]) => (wet ? W.dry(x + dx, y + dy) || (W.wall(x + dx, y + dy) && ctx.sq.on(x + dx, y + dy)) : W.wet(x + dx, y + dy)));
    if (!sides.length) continue;
    const prof = sides.map(([, , s]) => EDGE_DEPTHS[ctx.roll(`shelf:${s}`, x, y) % EDGE_DEPTHS.length]!);
    for (let iy = 0; iy < C; iy++) for (let ix = 0; ix < C; ix++) {
      let d = 99;
      sides.forEach(([, , s], k) => {
        const [i, kk] = s === 'n' ? [ix, iy] : s === 's' ? [ix, C - 1 - iy] : s === 'w' ? [iy, ix] : [iy, C - 1 - ix];
        d = Math.min(d, kk - prof[k]![i]! + (wet ? 0 : 2));
      });
      const X = x * C + ix, Y = y * C + iy;
      if (wet) {
        if (d < 0) { const m = mossAt(X, Y); if (m) out.set(X, Y, m); }
        else if (d === 0) out.set(X, Y, wetC);
      } else if (d < 0) { const m = mossAt(X, Y); if (m) out.set(X, Y, m); }
    }
  }
}

export const hasWater = (ctx: Ctx, grounds: Grounds): boolean => new Water(ctx, grounds).any();
